import React, { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { context as RehearsalContext } from '../../context/rehearsal'
import { context as AuthContext } from '../../context/auth'
import { PrimaryButton } from '../../styled/button'
import LinkButton from '../../styled/link-button'
import {
  RehearsalControlsContainer,
  RehearsalStatus,
  StatusLabel,
  StatusInfo,
  ButtonGroup
} from '../../styled/rehearsal-controls'

const RehearsalControls = ({ groupId, activeRehearsal }) => {
  const navigate = useNavigate()
  const { user } = useContext(AuthContext)
  const {
    startRehearsal,
    joinRehearsal,
    endRehearsal,
    loading,
    fetchActiveRehearsals
  } = useContext(RehearsalContext)

  const [ownerEmail, setOwnerEmail] = React.useState(null)

  useEffect(() => {
    // Fetch active rehearsals when component mounts or groupId changes
    if (groupId) {
      fetchActiveRehearsals()
    }
  }, [groupId, fetchActiveRehearsals])

  // Fetch owner email when activeRehearsal changes
  useEffect(() => {
    const fetchOwnerEmail = async () => {
      if (activeRehearsal && typeof activeRehearsal.owner === 'string') {
        try {
          // If owner is just an ID, we need to fetch the user info
          // For now, let's check if the current user is the owner
          if (user && activeRehearsal.owner === user._id) {
            setOwnerEmail(user.email)
          } else {
            setOwnerEmail('Another User')
          }
        } catch (err) {
          console.error('Error fetching owner info:', err)
          setOwnerEmail('Unknown')
        }
      } else if (activeRehearsal && typeof activeRehearsal.owner === 'object') {
        setOwnerEmail(activeRehearsal.owner.email || 'Unknown')
      } else {
        setOwnerEmail(null)
      }
    }

    fetchOwnerEmail()
  }, [activeRehearsal, user])

  const handleStartRehearsal = async () => {
    try {
      await startRehearsal(groupId)
    } catch (err) {
      console.error('Failed to start rehearsal:', err)
      window.alert('Failed to start rehearsal. Please try again.')
    }
  }

  const handleJoinRehearsal = async () => {
    if (!activeRehearsal) return

    try {
      await joinRehearsal(activeRehearsal._id)
      // Navigate to the rehearsal view
      navigate(`/rehearse/${activeRehearsal._id}`)
    } catch (err) {
      console.error('Failed to join rehearsal:', err)
      window.alert('Failed to join rehearsal. Please try again.')
    }
  }

  const handleEndRehearsal = async () => {
    try {
      await endRehearsal()
    } catch (err) {
      console.error('Failed to end rehearsal:', err)
      window.alert('Failed to end rehearsal. Please try again.')
    }
  }

  // Handle both populated owner object and owner ID string
  const ownerId = typeof activeRehearsal?.owner === 'object' ? activeRehearsal.owner._id : activeRehearsal?.owner
  const isOwner = activeRehearsal && user && ownerId === user._id

  return (
    <RehearsalControlsContainer>
      {activeRehearsal && (
        <RehearsalStatus>
          <StatusLabel>Active Rehearsal</StatusLabel>
          <StatusInfo>
            Group: {activeRehearsal.group?.title || activeRehearsal.group?.name || 'Unknown Group'}
          </StatusInfo>
          <StatusInfo>
            Owner: {ownerEmail || 'Loading...'}
          </StatusInfo>
        </RehearsalStatus>
      )}

      <ButtonGroup>
        {!activeRehearsal && (
          <PrimaryButton onClick={handleStartRehearsal} disabled={loading}>
            {loading ? 'Starting...' : 'Start Rehearsal'}
          </PrimaryButton>
        )}

        {activeRehearsal && !isOwner && (
          <PrimaryButton onClick={handleJoinRehearsal} disabled={loading}>
            {loading ? 'Joining...' : 'Join Rehearsal'}
          </PrimaryButton>
        )}

        {activeRehearsal && isOwner && (
          <>
            <PrimaryButton onClick={handleEndRehearsal} disabled={loading}>
              {loading ? 'Ending...' : 'End Rehearsal'}
            </PrimaryButton>
            <LinkButton>
              Rehearsal Active
            </LinkButton>
          </>
        )}
      </ButtonGroup>
    </RehearsalControlsContainer>
  )
}

export default RehearsalControls
